import { navigate, RouteComponentProps } from '@gatsbyjs/reach-router';
import { useTheme } from 'styled-components';
import React, { useEffect, useState } from 'react';
import EmptyStateComponent from '~/components/template/EmptyStateComponent';
import AppDetailsContainer from '~/components/page/Apps/components/AppDetailsContainer';
import TEST_ID from './index.testid';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import {
  AppStatusRealworksFieldsFragment,
  Exact,
  GetAppStatusRealworksQuery,
  RealworksRelatieSyncType,
  UpdateRealworksAppStatusMutationVariables,
  useGetAppStatusRealworks_RealworksKenmerkenQuery,
  useUpdateRealworksAppStatusMutation,
} from '~/graphql/types';
import { Heading4, Body, Variant } from '~/components/atom/Typography';
import type { Kenmerk, KenmerkLookup } from './types';
import formatToastMessage from '~/util/formatToastMessage';
import useAddToast from '~/hooks/useAddToast';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { equals, isEmpty, isNil } from 'ramda';
import TextButton from '~/components/atom/TextButton';
import { animated, useSpring } from 'react-spring';
import { OperationVariables, WatchQueryOptions } from '@apollo/client';
import CaretDropdownButton from '~/components/molecule/CaretDropdownButton';
import { isValid } from '~/util/Validation/Tag';
import RadioButton from '~/components/molecule/RadioButton';
import { ANIMATION_CONFIG } from '~/styles/constants';
import Catalog from '~/Catalog';
import NoKenmerken from './components/NoKenmerken';
import Checkbox from '~/components/molecule/Checkbox';
import createKenmerkLookup from './util/createKenmerkLookup';
import RealworksSelectableAccordion from '../RealworksSelectableAccordion';
import generateMetadataForSyncType from './util/generateMetadataForSyncType';
import { assertNever } from '~/util/assertNever';
import hasValue from '~/util/hasValue';
import useErrorReporter from '~/hooks/useErrorReporter';

const text = {
  header: 'Synchronisatie',
  emptyState: {
    header: (
      <>
        Je hebt nog geen actieve koppelingen
        <br /> met Realworks.
      </>
    ),
    button: 'Toevoegen',
  },
  description: `Kies welke contacten je wilt synchroniseren met ${Catalog.companyName}. Je kunt ook kenmerken uit Realworks automatisch omzetten naar een tag in ${Catalog.companyName}.`,
  syncOptions: {
    title: 'Welke relaties wil je synchroniseren?',
    full: 'Alle relaties',
    limited: 'Alleen relaties met geselecteerde kenmerken',
    excluding: 'Alle relaties, behalve relaties met geselecteerde kenmerken',
  },
  selectionDescription: 'Relaties kiezen voor synchronisatie',
  buttons: {
    deactivate: 'Deactiveer synchronisatie',
    save: 'Opslaan',
    saveAndActivate: 'Opslaan en activeren',
    saveAndDeactivate: 'Opslaan en deactiveren',
  },
  toasts: {
    error: {
      deactivate:
        'Er is iets fout gegaan bij het deactiveren van de synchronisatie, probeer het later opnieuw',
      sync: 'Er is iets fout gegaan bij het synchroniseren, probeer het later opnieuw',
      saveDeactivate: (isRelatieSyncEnabled: boolean) =>
        `Er is iets fout gegaan bij het ${isRelatieSyncEnabled ? 'opslaan en deactiveren van de synchronisatie' : 'opslaan van de wijzigingen'}, probeer het later opnieuw`,
    },

    success: {
      deactivate: 'Synchronisatie gedeactiveerd',
      activate: 'Synchronisatie geactiveerd',
      save: 'Wijzigingen opgeslagen',
    },
  },
  includeRelatiesWithoutKenmerken:
    'Ook relaties zonder kenmerken synchroniseren',
};

type Props = {
  appStatus: AppStatusRealworksFieldsFragment;
  updateQuery: <
    TVars extends OperationVariables = Exact<{ accountId: string }>,
  >(
    mapFn: (
      previousQueryResult: GetAppStatusRealworksQuery,
      options: Pick<
        WatchQueryOptions<TVars, GetAppStatusRealworksQuery>,
        'variables'
      >,
    ) => GetAppStatusRealworksQuery,
  ) => void;
} & RouteComponentProps;

const Synchronization: React.FCC<Props> = ({ appStatus, updateQuery }) => {
  const theme = useTheme();
  const addToast = useAddToast();
  const { id: accountId } = useCurrentAccount();
  const reportError = useErrorReporter();

  const [hasValidationError, setHasValidationError] = useState(false);

  const initialSyncType = ((
    typename:
      | 'AppStatus_Realworks_FullSync'
      | 'AppStatus_Realworks_LimitToKenmerkenSync'
      | 'AppStatus_Realworks_FullExcludingKenmerkenSync'
      | null
      | undefined,
  ): RealworksRelatieSyncType => {
    if (isNil(typename)) {
      // noop but still good to know. It means the migration has failed for this account.
      reportError.captureException(
        new Error('syncType cannot be null or undefined'),
      );
      throw new Error('syncType cannot be null or undefined');
    }
    switch (typename) {
      case 'AppStatus_Realworks_FullSync':
        return RealworksRelatieSyncType.Full;
      case 'AppStatus_Realworks_LimitToKenmerkenSync':
        return RealworksRelatieSyncType.LimitToKenmerken;
      case 'AppStatus_Realworks_FullExcludingKenmerkenSync':
        return RealworksRelatieSyncType.FullExcludingKenmerken;
      default:
        return assertNever(typename, 'syncTypeToRealworksRelatieSyncType');
    }
  })(appStatus.relatieSync.syncMetadata?.__typename);

  const initialRelatiesWithoutKenmerken =
    appStatus.relatieSync.syncMetadata?.__typename !==
    'AppStatus_Realworks_LimitToKenmerkenSync'
      ? undefined
      : {
          include:
            appStatus.relatieSync.syncMetadata.relatiesWithoutKenmerken.include,
        };

  const [syncType, setSyncType] =
    useState<RealworksRelatieSyncType>(initialSyncType);
  const [relatiesWithoutKenmerken, setRelatiesWithoutKenmerken] = useState<{
    include: boolean;
  }>(initialRelatiesWithoutKenmerken ?? { include: false });

  const isRelatieSyncEnabled = appStatus.relatieSync.enabled;

  const styles = useSpring({
    from: {
      transform: isRelatieSyncEnabled ? 'translateX(-40px)' : 'translateX(0px)',
      opacity: isRelatieSyncEnabled ? 0 : 1,
    },
    to: {
      transform: isRelatieSyncEnabled ? 'translateX(0px)' : 'translateX(-40px)',
      opacity: isRelatieSyncEnabled ? 1 : 0,
    },
    config: ANIMATION_CONFIG.config,
  });

  const [initialKenmerkLookup, setInitialKenmerkLookup] =
    useState<KenmerkLookup | null>(null);
  const [updatedKenmerkLookup, setUpdatedKenmerkLookup] =
    useState<KenmerkLookup | null>(null);

  const { data, loading } = useGetAppStatusRealworks_RealworksKenmerkenQuery({
    variables: { accountId },
  });

  const realworksKenmerken = data?.getAppStatusRealworks_RealworksKenmerken;
  const kenmerkMapping = appStatus?.relatieSync?.syncMetadata?.kenmerkMapping;

  useEffect(() => {
    if (realworksKenmerken) {
      const initialLookup = createKenmerkLookup({
        kenmerkMapping,
        realworksKenmerken,
      });
      setInitialKenmerkLookup(initialLookup);
      setUpdatedKenmerkLookup(initialLookup);
    }
  }, [kenmerkMapping, realworksKenmerken]);

  const [updateRealworksAppStatus, { loading: updating }] =
    useUpdateRealworksAppStatusMutation();

  const original = {
    ...initialKenmerkLookup,
    syncType: initialSyncType,
    relatiesWithoutKenmerken: initialRelatiesWithoutKenmerken,
  };
  const updated = {
    ...updatedKenmerkLookup,
    syncType,
    relatiesWithoutKenmerken: relatiesWithoutKenmerken,
  };

  const hasChanges = !equals(original, updated);

  const onUpdate = (
    variables: UpdateRealworksAppStatusMutationVariables,
    { errorToast, successToast }: { errorToast: string; successToast: string },
  ) => {
    if (updatedKenmerkLookup) {
      return updateRealworksAppStatus({
        variables,
      }).then(({ errors, data }) => {
        if (errors && errors.length !== 0) {
          addToast([formatToastMessage(errorToast, 'danger')]);
          return;
        }

        if (!data) return;

        updateQuery(prevRes => ({
          ...prevRes,
          getAppStatusRealworks: data.updateRealworksAppStatus,
        }));

        addToast([formatToastMessage(successToast, 'success')]);
      });
    }

    return;
  };

  const onChange = ({
    groupName,
    kenmerkList,
  }: {
    groupName: string;
    kenmerkList: Array<Kenmerk>;
  }) => {
    setHasValidationError(
      kenmerkList.some(
        // Only validate when tag is not null and empty
        ({ tag }) => !isNil(tag) && !isEmpty(tag) && !isValid(tag),
      ),
    );
    setUpdatedKenmerkLookup(prevState => ({
      ...prevState,
      [groupName]: kenmerkList,
    }));
  };

  if (loading) return null;
  if (!data || appStatus?.enabled === false)
    return (
      <AppDetailsContainer
        header={text.header}
        icon="reload"
        loading={updating}
        dataTestId={TEST_ID.CONTAINER}
      >
        <EmptyStateComponent
          header={text.emptyState.header}
          illustration="box"
          buttonProps={{
            label: text.emptyState.button,
            onClick: () =>
              navigate('/-/apps/realworks/wizard/', { replace: true }),
          }}
          blobColor={theme.color('tertiary', 'light')}
        />
      </AppDetailsContainer>
    );

  if (!realworksKenmerken || !hasValue(updatedKenmerkLookup)) {
    return (
      <AppDetailsContainer
        header={text.header}
        loading={false}
        dataTestId={TEST_ID.CONTAINER}
      >
        <NoKenmerken
          isSyncEnabled={isRelatieSyncEnabled}
          updateQuery={updateQuery}
        />
      </AppDetailsContainer>
    );
  }

  return (
    <AppDetailsContainer
      header={text.header}
      loading={false}
      dataTestId={TEST_ID.CONTAINER}
    >
      <Body margin={['xl', null]}>
        {text.description}
        <br />
      </Body>
      <Heading4 variant={Variant.primary}>{text.syncOptions.title}</Heading4>

      <JustificationContainer
        direction="column"
        gap="base"
        margin={[null, null, 'l', null]}
      >
        <RadioButton
          checked={syncType === RealworksRelatieSyncType.Full}
          onChange={() => setSyncType(RealworksRelatieSyncType.Full)}
          label={text.syncOptions.full}
          dataTestId={TEST_ID.FULL_OPTION}
        />
        <RadioButton
          checked={syncType === RealworksRelatieSyncType.LimitToKenmerken}
          onChange={() =>
            setSyncType(RealworksRelatieSyncType.LimitToKenmerken)
          }
          label={text.syncOptions.limited}
          dataTestId={TEST_ID.LIMITED_TO_KENMERKEN_OPTION}
        />
        {syncType === RealworksRelatieSyncType.LimitToKenmerken && (
          <JustificationContainer
            direction="column"
            gap="base"
            margin={[null, null, null, 'l']}
          >
            <Checkbox
              value={relatiesWithoutKenmerken.include}
              onChange={() =>
                setRelatiesWithoutKenmerken(prev => ({
                  ...prev,
                  include: !prev.include,
                }))
              }
              label={text.includeRelatiesWithoutKenmerken}
            />
          </JustificationContainer>
        )}
        <RadioButton
          checked={syncType === RealworksRelatieSyncType.FullExcludingKenmerken}
          onChange={() =>
            setSyncType(RealworksRelatieSyncType.FullExcludingKenmerken)
          }
          label={text.syncOptions.excluding}
          dataTestId={TEST_ID.EXCLUDING_KENMERKEN_OPTION}
        />
      </JustificationContainer>

      <Heading4 margin={[null, null, 's', null]} variant={Variant.primary}>
        {text.selectionDescription}
      </Heading4>
      <RealworksSelectableAccordion
        kenmerkLookup={updatedKenmerkLookup}
        onChange={onChange}
        selectable={
          syncType === RealworksRelatieSyncType.LimitToKenmerken ||
          syncType === RealworksRelatieSyncType.FullExcludingKenmerken
        }
        syncType={syncType}
      />

      <JustificationContainer
        justification={styles.opacity ? 'space-between' : 'end'}
        margin={['l', null, null]}
      >
        <TextButton
          label={text.buttons.deactivate}
          appearance="danger"
          disabled={updating}
          onClick={() =>
            onUpdate(
              {
                accountId,
                updateRelatieSync: {
                  enabled: false,
                  syncMetadata: generateMetadataForSyncType({
                    syncType,
                    updatedKenmerkLookup,
                    relatiesWithoutKenmerken,
                  }),
                },
              },
              {
                errorToast: text.toasts.error.deactivate,
                successToast: text.toasts.success.deactivate,
              },
            )
          }
          dataTestId={TEST_ID.DEACTIVATE_BUTTON}
          as={animated.button}
          style={{ alignSelf: 'flex-end', ...(styles as any) }}
        />

        <CaretDropdownButton
          mainButtonOption={{
            label: isRelatieSyncEnabled
              ? text.buttons.save
              : text.buttons.saveAndActivate,
            onClickAction: () =>
              onUpdate(
                {
                  accountId,
                  updateRelatieSync: {
                    enabled: true,
                    syncMetadata: generateMetadataForSyncType({
                      syncType,
                      updatedKenmerkLookup,
                      relatiesWithoutKenmerken,
                    }),
                  },
                },
                {
                  errorToast: text.toasts.error.sync,
                  successToast: !isRelatieSyncEnabled
                    ? text.toasts.success.activate
                    : text.toasts.success.save,
                },
              ),
          }}
          dropdownOptions={[
            {
              label: isRelatieSyncEnabled
                ? text.buttons.saveAndDeactivate
                : text.buttons.save,
              onClickAction: () =>
                onUpdate(
                  {
                    accountId,
                    updateRelatieSync: {
                      enabled: false,
                      syncMetadata: generateMetadataForSyncType({
                        syncType,
                        updatedKenmerkLookup,
                        relatiesWithoutKenmerken,
                      }),
                    },
                  },
                  {
                    errorToast:
                      text.toasts.error.saveDeactivate(isRelatieSyncEnabled),
                    successToast: isRelatieSyncEnabled
                      ? text.toasts.success.deactivate
                      : text.toasts.success.save,
                  },
                ),
              type: !hasChanges ? 'DISABLED' : undefined,
            },
          ]}
          dataTestId={TEST_ID.SAVE_BUTTON}
          loading={updating}
          disabled={(!hasChanges && isRelatieSyncEnabled) || hasValidationError}
        />
      </JustificationContainer>
    </AppDetailsContainer>
  );
};

export default Synchronization;
